/**
 * JsShaderPreviewDialog: shows a live preview of a JS shader script inside a
 * modal dialog.
 *
 * Spawns a standalone shaderWorker to eval the script and receive the GLSL
 * strings + initial uniform values. Then creates a minimal Three.js scene
 * (rotating sphere + ambient light) using the resulting ShaderMaterial,
 * rendered into a visible <canvas> via WebGL. Drives tick() via a rAF loop.
 *
 * GLSL compilation errors (from Three.js / WebGL) are caught and shown inline.
 * Worker eval errors (bad JS syntax etc.) are also displayed.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Cpu, Play, Square, RotateCcw, AlertCircle } from 'lucide-react';
import type { ProjectFile } from '@/components/files/FileManager';

interface JsShaderPreviewDialogProps {
  file: ProjectFile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type WorkerStatus = 'idle' | 'starting' | 'running' | 'error' | 'stopped';

// ---------------------------------------------------------------------------
// Preview canvas — owns the Three.js scene and the shader worker
// ---------------------------------------------------------------------------

function JsShaderPreviewCanvas({
  file,
  onStatusChange,
  onError,
  stopped,
}: {
  file: ProjectFile;
  onStatusChange: (status: WorkerStatus) => void;
  onError: (error: string | null) => void;
  stopped: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const workerRef = useRef<Worker | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const stoppedRef = useRef(stopped);
  const startTimeRef = useRef(performance.now());
  stoppedRef.current = stopped;

  const cleanup = useCallback(() => {
    // Stop rAF loop
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    // Terminate worker
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'STOP' });
      workerRef.current.terminate();
      workerRef.current = null;
    }
    // Dispose Three.js objects
    if (meshRef.current) {
      meshRef.current.geometry.dispose();
      if (meshRef.current.material instanceof THREE.Material) {
        meshRef.current.material.dispose();
      }
      meshRef.current = null;
    }
    if (rendererRef.current) {
      rendererRef.current.dispose();
      rendererRef.current = null;
    }
    sceneRef.current = null;
    cameraRef.current = null;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof file.content !== 'string') return;

    cleanup();

    const scriptCode = file.content;
    const W = 512;
    const H = 512;

    // ── Set up Three.js renderer ──────────────────────────────────────────
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    } catch {
      onStatusChange('error');
      onError('Could not create WebGL context');
      return;
    }
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, W / H, 0.1, 1000);
    camera.position.set(0, 0, 60);
    cameraRef.current = camera;

    // Simple ambient + directional light so the mesh has some depth
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const dir = new THREE.DirectionalLight(0xffffff, 0.8);
    dir.position.set(1, 2, 3);
    scene.add(dir);

    // Placeholder material while the worker loads
    const placeholder = new THREE.MeshStandardMaterial({
      color: 0x334455,
      roughness: 0.8,
    });
    const geometry = new THREE.SphereGeometry(20, 64, 64);
    const mesh = new THREE.Mesh(geometry, placeholder);
    scene.add(mesh);
    meshRef.current = mesh;

    // Render one placeholder frame immediately
    renderer.render(scene, camera);

    onStatusChange('starting');
    onError(null);
    startTimeRef.current = performance.now();

    // ── Spawn shader worker ───────────────────────────────────────────────
    const worker = new Worker(
      new URL('../../worker/shaderWorker.ts', import.meta.url),
      { type: 'module' },
    );
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent) => {
      const { type } = event.data as { type: string };

      if (type === 'READY') {
        const {
          vertexShader,
          fragmentShader,
          initialUniforms,
          hasTick,
        } = event.data as {
          vertexShader: string;
          fragmentShader: string;
          initialUniforms: Record<string, unknown>;
          hasTick: boolean;
        };

        if (!meshRef.current || !rendererRef.current || !sceneRef.current || !cameraRef.current) return;

        // Build uniforms map: inject uColor, then layer script values
        const uniforms: Record<string, { value: unknown }> = {
          uColor: { value: new THREE.Color(0.2, 0.6, 1.0) },
        };
        for (const [k, v] of Object.entries(initialUniforms)) {
          uniforms[k] = { value: v };
        }

        // Create ShaderMaterial and handle GLSL compile errors
        let shaderMaterial: THREE.ShaderMaterial;
        try {
          shaderMaterial = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          onStatusChange('error');
          onError(`ShaderMaterial creation failed: ${msg}`);
          return;
        }

        // Swap placeholder material for the real shader
        const oldMat = meshRef.current.material as THREE.Material;
        meshRef.current.material = shaderMaterial;
        oldMat.dispose();

        onStatusChange('running');

        // ── rAF render loop ───────────────────────────────────────────────
        let glslErrorLogged = false;

        const loop = () => {
          if (stoppedRef.current || !rendererRef.current || !sceneRef.current || !cameraRef.current || !meshRef.current) return;

          rafIdRef.current = requestAnimationFrame(loop);

          // Slow rotation so the user can see the shader from all angles
          meshRef.current.rotation.y += 0.005;
          meshRef.current.rotation.x += 0.002;

          // Send TICK to worker if the script has a tick function
          if (hasTick && workerRef.current) {
            const elapsedMs = performance.now() - startTimeRef.current;
            workerRef.current.postMessage({ type: 'TICK', elapsedMs });
          }

          // Render — catch GLSL errors via the renderer's info
          try {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
          } catch (err) {
            if (!glslErrorLogged) {
              glslErrorLogged = true;
              const msg = err instanceof Error ? err.message : String(err);
              onStatusChange('error');
              onError(`Render error: ${msg}`);
            }
          }

          // Check for WebGL program errors after first render
          if (!glslErrorLogged) {
            const programs = (rendererRef.current as unknown as { info: { programs: Array<{ diagnostics?: { fragmentShader?: { log?: string }; vertexShader?: { log?: string } } }> } | null }).info?.programs;
            if (programs) {
              for (const prog of programs) {
                const fragLog = prog.diagnostics?.fragmentShader?.log;
                const vertLog = prog.diagnostics?.vertexShader?.log;
                if (fragLog || vertLog) {
                  glslErrorLogged = true;
                  onStatusChange('error');
                  onError(
                    [vertLog && `Vertex shader:\n${vertLog}`, fragLog && `Fragment shader:\n${fragLog}`]
                      .filter(Boolean)
                      .join('\n\n'),
                  );
                }
              }
            }
          }
        };
        rafIdRef.current = requestAnimationFrame(loop);
      }

      if (type === 'UNIFORMS') {
        // Apply serialized uniform values to the live material
        if (!meshRef.current) return;
        const mat = meshRef.current.material;
        if (!(mat instanceof THREE.ShaderMaterial)) return;
        const { values } = event.data as { values: Record<string, unknown> };
        for (const [k, v] of Object.entries(values)) {
          if (k in mat.uniforms) {
            mat.uniforms[k].value = v;
          } else {
            mat.uniforms[k] = { value: v };
          }
        }
      }

      if (type === 'ERROR') {
        const msg = (event.data as { message: string }).message;
        onStatusChange('error');
        onError(msg);
      }
    };

    worker.onerror = (err) => {
      onStatusChange('error');
      onError(err.message || 'Worker failed to load');
    };

    // Start the script
    worker.postMessage({ type: 'START', scriptCode });

    return () => {
      cleanup();
    };
  }, [file, cleanup, onStatusChange, onError]);

  // Handle stop
  useEffect(() => {
    if (stopped) {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'STOP' });
        workerRef.current.terminate();
        workerRef.current = null;
      }
    }
  }, [stopped]);

  return (
    <div className="flex w-full items-center justify-center">
      <canvas
        ref={canvasRef}
        className="rounded-md border border-border"
        style={{
          maxWidth: '100%',
          maxHeight: '320px',
          width: '320px',
          height: '320px',
          background: '#1a1a2e',
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog shell
// ---------------------------------------------------------------------------

export function JsShaderPreviewDialog({
  file,
  open,
  onOpenChange,
}: JsShaderPreviewDialogProps) {
  const [status, setStatus] = useState<WorkerStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [stopped, setStopped] = useState(false);
  const [restartKey, setRestartKey] = useState(0);

  // Reset state when dialog opens or file changes
  useEffect(() => {
    if (open) {
      setStatus('idle');
      setError(null);
      setStopped(false);
    }
  }, [open, file]);

  if (!file) return null;

  const handleStop = () => {
    setStopped(true);
    setStatus('stopped');
  };

  const handleRestart = () => {
    setStopped(false);
    setStatus('idle');
    setError(null);
    setRestartKey((k) => k + 1);
  };

  const statusBadge = () => {
    switch (status) {
      case 'starting':
        return (
          <Badge variant="outline" className="border-yellow-500/50 text-yellow-500">
            Starting...
          </Badge>
        );
      case 'running':
        return (
          <Badge variant="outline" className="border-green-500/50 text-green-500">
            Running
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="border-red-500/50 text-red-500">
            Error
          </Badge>
        );
      case 'stopped':
        return (
          <Badge variant="outline" className="border-muted-foreground/50 text-muted-foreground">
            Stopped
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-xl flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-violet-400" />
            <span className="break-all">{file.filename}</span>
            {statusBadge()}
          </DialogTitle>
        </DialogHeader>

        {/* Metadata bar */}
        <div className="flex flex-wrap items-center gap-4 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <span>GLSL shader script</span>
          <span>Preview: rotating sphere</span>
          <span className="text-violet-400/80">uColor = vec3(0.2, 0.6, 1.0)</span>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="mb-1 font-medium">Shader error</div>
              <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px]">
                {error}
              </pre>
            </div>
          </div>
        )}

        {/* WebGL preview canvas — only mount when dialog is open */}
        {open && (
          <JsShaderPreviewCanvas
            key={restartKey}
            file={file}
            onStatusChange={setStatus}
            onError={setError}
            stopped={stopped}
          />
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <div className="flex gap-2">
            {status === 'running' ? (
              <Button variant="outline" size="sm" onClick={handleStop}>
                <Square className="mr-1.5 h-3.5 w-3.5" />
                Stop
              </Button>
            ) : status === 'stopped' || status === 'error' ? (
              <Button variant="outline" size="sm" onClick={handleRestart}>
                <Play className="mr-1.5 h-3.5 w-3.5" />
                Restart
              </Button>
            ) : null}
            {status === 'running' && (
              <Button variant="outline" size="sm" onClick={handleRestart}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Restart
              </Button>
            )}
          </div>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
